// components/SongRequestModal.js - Guest song request modal with YouTube search
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMusic, FiSearch, FiX, FiUser } from 'react-icons/fi';
import '../styles/SongRequestModal.scss';

const SongRequestModal = ({ onClose, onSubmitRequest, isSubmitting, eventDetails }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [message, setMessage] = useState('');

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      // Call YouTube search API through your backend
      const response = await fetch(`/api/youtube/search?q=${encodeURIComponent(searchQuery)}`);
      const data = await response.json();
      setSearchResults(data.items || []);
    } catch (error) {
      console.error('Error searching songs:', error);
      alert('Failed to search. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedSong) {
      alert('Please select a song');
      return;
    }

    onSubmitRequest({
      videoId: selectedSong.id.videoId,
      title: selectedSong.snippet.title,
      channelTitle: selectedSong.snippet.channelTitle,
      thumbnail: selectedSong.snippet.thumbnails.medium.url,
      message: message.trim(),
    });
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="song-request-modal"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="header-left">
            <FiMusic size={24} />
            <div>
              <h2>Request a Song</h2>
              <p>Search and request songs for the event</p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        <div className="modal-body">
          {/* Search Section */}
          <div className="search-section">
            <div className="search-input-container">
              <FiSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search for a song or artist..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              {searchQuery && (
                <button
                  className="clear-search-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                >
                  <FiX size={16} />
                </button>
              )}
            </div>
            <button
              className="search-btn"
              onClick={handleSearch}
              disabled={!searchQuery.trim() || isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="search-results">
              <h3>Search Results</h3>
              <div className="results-list">
                {searchResults.map((result) => (
                  <div
                    key={result.id.videoId}
                    className={`result-card ${selectedSong?.id.videoId === result.id.videoId ? 'selected' : ''}`}
                    onClick={() => setSelectedSong(result)}
                  >
                    <img
                      src={result.snippet.thumbnails.medium.url}
                      alt={result.snippet.title}
                      className="result-thumbnail"
                    />
                    <div className="result-info">
                      <h4>{result.snippet.title}</h4>
                      <div className="result-meta">
                        <span className="channel">
                          <FiUser size={12} />
                          {result.snippet.channelTitle}
                        </span>
                      </div>
                    </div>
                    {selectedSong?.id.videoId === result.id.videoId && (
                      <div className="selected-badge">
                        <FiMusic size={16} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected Song & Message */}
          {selectedSong && (
            <div className="selected-song-section">
              <h3>Selected Song</h3>
              <div className="selected-song-card">
                <img
                  src={selectedSong.snippet.thumbnails.medium.url}
                  alt={selectedSong.snippet.title}
                  className="selected-thumbnail"
                />
                <div className="selected-info">
                  <h4>{selectedSong.snippet.title}</h4>
                  <p>{selectedSong.snippet.channelTitle}</p>
                </div>
              </div>

              <div className="message-section">
                <label>Add a message (optional)</label>
                <textarea
                  className="message-input"
                  placeholder="Why do you want to hear this song?"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={200}
                  rows={3}
                />
                <span className="char-count">{message.length}/200</span>
              </div>
            </div>
          )}

          {/* Empty State */}
          {searchResults.length === 0 && !isSearching && (
            <div className="empty-state">
              <FiMusic size={48} />
              <p>Search for songs to request</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={!selectedSong || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SongRequestModal;
